import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import api, { ACTIVE_COMPANY_KEY } from '../../services/api';
import type { CompanySummary } from '../../types/workspace';

type CompanyContextValue = {
  companies: CompanySummary[];
  active: CompanySummary | null;
  activeId: string | null;
  loading: boolean;
  /** Bumps when the active company changes so pages remount and refetch. */
  version: number;
  select: (id: string) => void;
  refresh: () => Promise<CompanySummary[]>;
};

const CompanyContext = createContext<CompanyContextValue | null>(null);

export function CompanyProvider({ children }: { children: ReactNode }) {
  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [activeId, setActiveId] = useState<string | null>(() => localStorage.getItem(ACTIVE_COMPANY_KEY));
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);

  const persist = (id: string | null) => {
    if (id) localStorage.setItem(ACTIVE_COMPANY_KEY, id);
    else localStorage.removeItem(ACTIVE_COMPANY_KEY);
    setActiveId(id);
  };

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get<{ companies: CompanySummary[] }>('/customer/companies');
      const list = data.companies || [];
      setCompanies(list);
      const stored = localStorage.getItem(ACTIVE_COMPANY_KEY);
      if (!list.some((c) => c.id === stored)) persist(list[0]?.id || null);
      return list;
    } catch {
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const select = useCallback((id: string) => {
    persist(id);
    setVersion((v) => v + 1);
  }, []);

  const value = useMemo<CompanyContextValue>(
    () => ({
      companies,
      active: companies.find((c) => c.id === activeId) || null,
      activeId,
      loading,
      version,
      select,
      refresh,
    }),
    [companies, activeId, loading, version, select, refresh],
  );

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

export function useCompanies(): CompanyContextValue {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error('useCompanies must be used inside CompanyProvider');
  return ctx;
}
